import { type SxProps, type Theme } from '@mui/material'
import {
  type DataGridProps,
  gridClasses,
  type GridColDef,
  type MuiEvent
} from '@mui/x-data-grid'

import {
  getLightMuiBackgroundColor,
  getLightMuiBackgroundColorHover
} from 'utils/colors'
import { isTestEnvironment } from 'utils/environment'

import type React from 'react'
import type { ScenarioRunStatus } from 'types/api/scenarios/runs/scenario-runs'

/** Reusable utilities, constants, etc for MUI DataGrid */
export enum RunStatusClassSuffix {
  FAILED = 'test-failed',
  PASSED = 'test-passed',
  UNSURE = 'test-unsure'
}

/** MUI doesn't have an easy way to find or get these,
 * provided for convenience here. Source:
 * `node_modules/@mui/x-data-grid/node/hooks/features/filter/gridFilterUtils.js` line 119
 * (version 5.17.14)
 * `const filterOperators = column.filterOperators;`
 */
export enum FilterOperatorValue {
  Contains = 'contains',
  Equals = 'equals',
  StartsWith = 'startsWith',
  EndsWith = 'endsWith',
  IsEmpty = 'isEmpty',
  IsNotEmpty = 'isNotEmpty',
  IsAnyOf = 'isAnyOf'
}

const DEFAULT_PAGE_SIZE = 10

export const defaultGridProps: Partial<DataGridProps> = {
  autoHeight: true,
  disableSelectionOnClick: true,
  disableVirtualization: isTestEnvironment(), // see https://github.com/mui/mui-x/issues/1519
  pageSize: DEFAULT_PAGE_SIZE,
  rowsPerPageOptions: [DEFAULT_PAGE_SIZE]
}

export const defaultColumnProps: Partial<GridColDef> = {
  disableColumnMenu: true,
  disableReorder: true,
  filterable: false,
  sortable: false
}

const sxGridDefault = {
  /**
   * Disable blue border outlining the cell with focus:
   * https://github.com/mui/mui-x/issues/8104#issuecomment-1453945311
   */
  [`& .${gridClasses.cell}:focus-within`]: {
    outline: 0
  },

  [`& .${gridClasses.root}`]: {
    whiteSpace: 'break-spaces'
  },
  [`& .${gridClasses.columnSeparator}`]: {
    display: 'none'
  }
}

export const sxClickableRow = {
  [`& .${gridClasses.row}`]: { cursor: 'pointer' }
}

export function applyRunStatusClassNames(status: ScenarioRunStatus) {
  if (status === 'Failed') {
    return 'test-failed'
  }

  if (status === 'Passed') {
    return 'test-passed'
  }

  return ''
}

export function getGridRowBackgroundStyles(
  classSuffix: RunStatusClassSuffix,
  color: 'error' | 'success' | 'warning',
  isClickable = false
): SxProps {
  return {
    [`& .${gridClasses.row}.${classSuffix}`]: (muiTheme: Theme) => {
      const colorValue = muiTheme.palette[color].light
      const baseBgColor = getLightMuiBackgroundColor(colorValue)
      const hoverBgColor = getLightMuiBackgroundColorHover(colorValue)

      return {
        backgroundColor: baseBgColor,
        ...(isClickable && {
          '&:hover': {
            backgroundColor: hoverBgColor
          }
        })
      }
    }
  }
}

export function applyRunStatusRowColors({
  clickableRow
}: {
  clickableRow?: boolean
} = {}) {
  return {
    ...getGridRowBackgroundStyles(
      RunStatusClassSuffix.FAILED,
      'warning',
      clickableRow
    ),
    ...getGridRowBackgroundStyles(
      RunStatusClassSuffix.PASSED,
      'success',
      clickableRow
    )
  }
}

export function getGridStyles(
  options: {
    clickableRow?: boolean
    cellPaddingY?: number
    hideBorder?: boolean
    hidePaginationDisplayedRows?: boolean
    minHeight?: number | string
  } = {}
) {
  const {
    clickableRow = false,
    cellPaddingY = 1,
    hideBorder = true,
    hidePaginationDisplayedRows = false,
    minHeight
  } = options

  return {
    ...sxGridDefault,
    ...(hideBorder && {
      border: 0
    }),
    [`& .${gridClasses.cell}`]: {
      py: cellPaddingY
    },
    ...(clickableRow && sxClickableRow),
    ...(hidePaginationDisplayedRows && {
      ['& .MuiTablePagination-displayedRows']: {
        display: 'none'
      }
    }),
    ...(minHeight !== undefined && {
      [`& .${gridClasses.main}`]: {
        minHeight
      }
    })
  }
}

/**
 * General DOM utility
 */
export function getIsTextSelected() {
  return Boolean(window.getSelection()?.toString())
}

/**
 * The callback action is allowed if
 * - the click target is the row itself. This enables other row actions without adding event.stopPropagation() to their onClick handlers
 * - no text is selected on the screen. This allows the user to copy text without navigating away before they're done
 */
export function safeRowClick(
  event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
  callback: () => unknown
) {
  // @ts-expect-error this is intentionally permissive
  const isDataGridCell = Array.from(event?.target?.classList ?? []).includes(
    gridClasses.cell
  )

  if (isDataGridCell && !getIsTextSelected()) {
    callback()
  }
}
